import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/auth/AuthProvider'

type TFn = (key: string, fallback: string) => string

function friendlyError(err: unknown, t: TFn): string {
  const code = (err as { code?: string })?.code || ''
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return t('auth.loginFailed', 'Unable to log in. Please check your credentials.')
    case 'auth/invalid-email':
      return t('auth.invalidEmail', 'Please enter a valid email')
    case 'auth/email-already-in-use':
      return t('auth.emailInUse', 'An account already exists for this email. Sign in instead.')
    case 'auth/weak-password':
      return t('auth.atLeast6Chars', 'Password must be at least 6 characters')
    case 'auth/too-many-requests':
      return t('auth.tooManyRequests', 'Too many attempts. Please try again later.')
    case 'auth/account-exists-with-different-credential':
      return t(
        'auth.accountExistsDifferentMethod',
        'An account already exists for this email with a different sign-in method. Try the method you used in the app — email & password, Google, or Apple.',
      )
    case 'auth/popup-blocked':
      return t('auth.popupBlocked', 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.')
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return ''
    default:
      return t('errors.generic', 'Something went wrong. Please try again.')
  }
}

export function SignIn() {
  const { t } = useTranslation()
  const { signInEmail, signUpEmail, signInGoogle, signInApple, resetPassword } = useAuth()
  const [mode, setMode] = useState<'signIn' | 'signUp'>('signIn')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  async function run(fn: () => Promise<void>) {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await fn()
      // On success the auth listener flips state and the router redirects.
    } catch (err) {
      const msg = friendlyError(err, t)
      if (msg) setError(msg)
    } finally {
      setBusy(false)
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    const fn = mode === 'signIn' ? signInEmail : signUpEmail
    void run(() => fn(email.trim(), password))
  }

  async function onForgot() {
    if (!email.trim()) {
      setError(t('auth.enterEmailFirst', 'Enter your email above first, then tap "Forgot password".'))
      return
    }
    await run(async () => {
      await resetPassword(email.trim())
      setNotice(t('auth.resetEmailSent', 'Password reset email sent. Check your inbox.'))
    })
  }

  return (
    <div className="flex h-full w-full items-center justify-center bg-bg p-md">
      <div className="w-full max-w-md animate-fade-in rounded-xl border border-border bg-card p-xl shadow-lg">
        <div className="mb-lg flex flex-col items-center gap-sm">
          <img src="/icon-192.png" alt="" className="h-20 w-20 rounded-[18px] shadow-md" />
          <h1 className="text-h2 font-bold text-text">My Keto</h1>
          <p className="text-center text-body-sm text-text-secondary">
            {mode === 'signIn'
              ? t('auth.signInSubtitle', 'Sign in to your My Keto account')
              : t('auth.createAccountSubtitle', 'Create your free My Keto account')}
          </p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-md">
          <label className="flex flex-col gap-xs">
            <span className="text-body-sm font-medium text-text-secondary">
              {t('auth.email', 'Email')}
            </span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-lg border border-border bg-bg-elevated px-md py-sm text-body text-text outline-none focus:border-primary"
            />
          </label>

          <label className="flex flex-col gap-xs">
            <span className="text-body-sm font-medium text-text-secondary">
              {t('auth.password', 'Password')}
            </span>
            <input
              type="password"
              autoComplete={mode === 'signIn' ? 'current-password' : 'new-password'}
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-lg border border-border bg-bg-elevated px-md py-sm text-body text-text outline-none focus:border-primary"
            />
          </label>

          {error ? <p className="text-body-sm text-trigger">{error}</p> : null}
          {notice ? <p className="text-body-sm text-safe">{notice}</p> : null}

          <button
            type="submit"
            disabled={busy}
            className="mt-xs rounded-lg bg-primary px-md py-sm text-button font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {mode === 'signIn' ? t('authSheet.signIn', 'Sign In') : t('auth.createAccount', 'Create Account')}
          </button>

          {mode === 'signIn' ? (
            <button
              type="button"
              onClick={() => void onForgot()}
              disabled={busy}
              className="text-body-sm text-primary hover:underline"
            >
              {t('auth.forgotPassword', 'Forgot password?')}
            </button>
          ) : null}
        </form>

        <div className="my-lg flex items-center gap-md">
          <span className="h-px flex-1 bg-border" />
          <span className="text-caption text-text-muted">{t('common.or', 'or')}</span>
          <span className="h-px flex-1 bg-border" />
        </div>

        <div className="flex flex-col gap-sm">
          <button
            onClick={() => void run(signInGoogle)}
            disabled={busy}
            className="rounded-lg border border-border bg-bg-elevated px-md py-sm text-button font-medium text-text hover:bg-bg-secondary disabled:opacity-60"
          >
            {t('authSheet.continueWithGoogle', 'Continue with Google')}
          </button>
          <button
            onClick={() => void run(signInApple)}
            disabled={busy}
            className="rounded-lg border border-border bg-bg-elevated px-md py-sm text-button font-medium text-text hover:bg-bg-secondary disabled:opacity-60"
          >
            {t('authSheet.continueWithApple', 'Continue with Apple')}
          </button>
        </div>

        <button
          onClick={() => {
            setMode(mode === 'signIn' ? 'signUp' : 'signIn')
            setError('')
            setNotice('')
          }}
          className="mt-lg w-full text-center text-body-sm text-primary hover:underline"
        >
          {mode === 'signIn'
            ? t('auth.dontHaveAccount', "Don't have an account? Create one")
            : t('auth.alreadyHaveAccount', 'Already have an account? Sign in')}
        </button>

        <p className="mt-md text-center text-caption text-text-muted">
          <a href="https://myketotracker.com/privacy" target="_blank" rel="noreferrer" className="hover:underline">
            {t('settings.privacyPolicy', 'Privacy Policy')}
          </a>
          {' · '}
          <a href="https://myketotracker.com/terms" target="_blank" rel="noreferrer" className="hover:underline">
            {t('settings.termsOfService', 'Terms of Service')}
          </a>
        </p>
      </div>
    </div>
  )
}
